/**
 * lottie-init.js — Self-hosted Lottie hydration controller
 * ─────────────────────────────────────────────────────────
 * • Finds all [data-lottie-asset] containers rendered by snippets/lottie.liquid
 * • Lazy-loads via IntersectionObserver (only inits when visible)
 * • Loads the self-hosted player on the first hydration request
 * • Handles Shopify section lifecycle (load / unload / reorder)
 * • Dedupes instances — no double renders
 * • On fetch/parse error → renders fallback image or inline SVG placeholder
 * • Respects prefers-reduced-motion
 */
(function () {
	"use strict";

	var SELECTOR = "[data-lottie-asset]";
	var INIT_ATTR = "data-lottie-ready";
	var instances = {}; // id → lottie animation instance
	var pending = new Map();
	var playerPromise = null;
	var initializer = document.currentScript;
	var playerSrc = initializer && initializer.getAttribute("data-lottie-player-src");

	/* ── Reduced motion check ── */
	var prefersReduced =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	/* Share one player request across all visible animations. */
	function getLottie() {
		var lib = window.lottie || window.bodymovin;
		return lib && typeof lib.loadAnimation === "function" ? lib : null;
	}

	function loadPlayer() {
		var lib = getLottie();
		if (lib) return Promise.resolve(lib);
		if (playerPromise) return playerPromise;
		if (!playerSrc) return Promise.reject(new Error("Missing Lottie player URL"));
		playerPromise = new Promise(function (resolve, reject) {
			var script = document.createElement("script");
			var finished = false;
			var timer = setTimeout(function () { finish(new Error("Lottie player timed out")); }, 10000);
			function finish(error) {
				if (finished) return;
				finished = true;
				clearTimeout(timer);
				script.onload = script.onerror = null;
				if (error) {
					script.remove();
					reject(error);
				} else resolve(getLottie());
			}
			script.async = true;
			script.src = playerSrc;
			script.onload = function () { finish(getLottie() ? null : new Error("Lottie player unavailable")); };
			script.onerror = function () { finish(new Error("Lottie player failed to load")); };
			document.head.appendChild(script);
		}).catch(function (error) {
			playerPromise = null;
			throw error;
		});
		return playerPromise;
	}

	function current(el, run) {
		return pending.get(el) === run && el.isConnected;
	}

	/* Render a single container, ignoring work from an unloaded section. */
	function hydrate(el) {
		if (!el || !el.isConnected || el.hasAttribute(INIT_ATTR)) return;
		el.setAttribute(INIT_ATTR, "true");
		var assetUrl = el.getAttribute("data-lottie-asset");
		var loop = el.getAttribute("data-lottie-loop") !== "false";
		var autoplay = el.getAttribute("data-lottie-autoplay") !== "false";
		var speed = parseFloat(el.getAttribute("data-lottie-speed")) || 1;
		var fallbackUrl = el.getAttribute("data-lottie-fallback") || "";
		var id = el.id || "lottie-" + Math.random().toString(36).slice(2, 8);
		el.id = id;
		if (!assetUrl) { showFallback(el, fallbackUrl); return; }
		if (prefersReduced) { autoplay = false; loop = false; }
		var run = { controller: new AbortController(), playerReady: false, retryTimer: null };
		pending.set(el, run);
		destroyInstance(id);
		loadPlayer().then(function (lib) {
			if (!current(el, run)) return;
			run.playerReady = true;
			return fetch(assetUrl, { signal: run.controller.signal })
				.then(function (res) {
					if (!res.ok) throw new Error("HTTP " + res.status);
					return res.json();
				})
				.then(function (animationData) {
					if (!current(el, run)) return;
					el.innerHTML = "";
					var anim = lib.loadAnimation({
						container: el, renderer: "svg", loop: loop, autoplay: autoplay,
						animationData: animationData,
						rendererSettings: { preserveAspectRatio: "xMidYMid meet", progressiveLoad: true },
					});
					anim.setSpeed(speed);
					instances[id] = anim;
					pending.delete(el);
					el.removeAttribute("data-lottie-retries");
				});
		}).catch(function () {
			if (!current(el, run)) return;
			var retryCount = parseInt(el.getAttribute("data-lottie-retries") || "0", 10);
			if (!run.playerReady && retryCount < 5) {
				el.setAttribute("data-lottie-retries", String(retryCount + 1));
				run.retryTimer = setTimeout(function () {
					if (!current(el, run)) return;
					pending.delete(el);
					el.removeAttribute(INIT_ATTR);
					hydrate(el);
				}, 500 * Math.pow(2, retryCount));
			} else {
				pending.delete(el);
				showFallback(el, fallbackUrl);
			}
		});
	}

	/* ── Fallback: image or inline SVG placeholder ── */
	function showFallback(el, fallbackUrl) {
		if (fallbackUrl) {
			el.innerHTML =
				'<img src="' +
				fallbackUrl +
				'" alt="Animation" loading="lazy" style="width:100%;height:100%;object-fit:contain;">';
		} else {
			// Minimal inline car SVG placeholder
			el.innerHTML =
				'<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;opacity:.35;">' +
				'<rect x="10" y="18" width="80" height="26" rx="6" fill="#98a2b3"/>' +
				'<rect x="30" y="6" width="40" height="20" rx="4" fill="#98a2b3"/>' +
				'<circle cx="30" cy="48" r="8" fill="#667085"/>' +
				'<circle cx="72" cy="48" r="8" fill="#667085"/>' +
				'<rect x="5" y="54" width="110" height="2" rx="1" fill="#d0d5dd"/>' +
				"</svg>";
		}
	}

	/* ── Destroy a specific instance ── */
	function destroyInstance(id) {
		if (instances[id]) {
			try {
				instances[id].destroy();
			} catch (_) {}
			delete instances[id];
		}
	}

	/* ── Destroy all (for section unload) ── */
	function resetElement(el) {
		var run = pending.get(el);
		if (run) {
			clearTimeout(run.retryTimer);
			run.controller.abort();
			pending.delete(el);
		}
		if (el.id) destroyInstance(el.id);
		el.removeAttribute(INIT_ATTR);
		el.removeAttribute("data-lottie-retries");
	}

	function destroyAll() {
		pending.forEach(function (run, el) { resetElement(el); });
		Object.keys(instances).forEach(destroyInstance);
		document.querySelectorAll(SELECTOR).forEach(resetElement);
	}

	/* ── Init all visible containers ── */
	function initAll() {
		var els = document.querySelectorAll(SELECTOR);
		if (!els.length) return;

		if ("IntersectionObserver" in window) {
			var observer = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						if (entry.isIntersecting) {
							hydrate(entry.target);
							observer.unobserve(entry.target);
						}
					});
				},
				{ rootMargin: "200px" },
			);
			els.forEach(function (el) {
				if (!el.hasAttribute(INIT_ATTR)) {
					observer.observe(el);
				}
			});
		} else {
			// Fallback: hydrate everything immediately
			els.forEach(hydrate);
		}
	}

	/* ── Re-init (for new section content) ── */
	function reinit() {
		// Find any un-initialised containers and hydrate them
		var els = document.querySelectorAll(SELECTOR + ":not([" + INIT_ATTR + "])");
		els.forEach(hydrate);
	}

	/* ── Shopify Section lifecycle ── */
	document.addEventListener("shopify:section:load", reinit);
	document.addEventListener("shopify:section:reorder", reinit);
	document.addEventListener("shopify:section:unload", function (e) {
		// Destroy instances inside the unloaded section
		if (e.target) {
			var els = e.target.querySelectorAll(SELECTOR);
			els.forEach(resetElement);
		}
	});

	/* ── Expose for programmatic use ── */
	window.__lottieInit = {
		hydrate: hydrate,
		reinit: reinit,
		destroyAll: destroyAll,
		instances: instances,
	};

	/* ── Boot ── */
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAll);
	} else {
		initAll();
	}
})();
